import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { History, TrendingUp, TrendingDown, RefreshCw, Bot as BotIcon, Target, CheckCircle, XCircle, Minus } from 'lucide-react';
import { toast } from 'sonner';
import { realTimeTradingPredictionService, type Bot, type BotRealtimeTrade } from '@/services/RealTimeTradingPredictionService';
import { formatDistanceToNow } from 'date-fns';

interface RealTimeTradesHistoryProps {
  selectedBotId?: string;
  onBotSelect?: (botId: string) => void;
}

export const RealTimeTradesHistory: React.FC<RealTimeTradesHistoryProps> = ({
  selectedBotId,
  onBotSelect
}) => {
  const [linkedBots, setLinkedBots] = useState<Bot[]>([]);
  const [currentBotId, setCurrentBotId] = useState<string>(selectedBotId || '');
  const [trades, setTrades] = useState<BotRealtimeTrade[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalTrades, setTotalTrades] = useState(0);
  const [page, setPage] = useState(0);
  const [limit] = useState(20);

  useEffect(() => {
    loadLinkedBots();
  }, []);

  useEffect(() => {
    if (currentBotId) {
      loadTradesHistory();
    }
  }, [currentBotId, page]);

  const loadLinkedBots = async () => {
    try {
      const bots = await realTimeTradingPredictionService.getBotsWithLinkedFiles();
      setLinkedBots(bots);
      
      if (bots.length > 0 && !currentBotId) {
        const firstBotId = bots[0].id;
        setCurrentBotId(firstBotId);
        onBotSelect?.(firstBotId);
      }
    } catch (error) {
      console.error('Error loading linked bots:', error);
      toast.error('Failed to load linked bots');
    }
  };

  const loadTradesHistory = async () => {
    if (!currentBotId) return;

    setIsLoading(true);
    try {
      const { trades: tradesData, total } = await realTimeTradingPredictionService.getBotRealtimeTrades(
        currentBotId, 
        limit, 
        page * limit
      );
      
      setTrades(tradesData);
      setTotalTrades(total);
    } catch (error) {
      console.error('Error loading trades history:', error);
      toast.error('Failed to load trades history');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBotChange = (botId: string) => {
    setCurrentBotId(botId);
    setPage(0);
    onBotSelect?.(botId);
  };

  const getResultBadge = (win: number) => {
    if (win === 1) {
      return (
        <Badge className="bg-green-500/10 text-green-700 border-green-200">
          <TrendingUp className="h-3 w-3 mr-1" />
          WIN
        </Badge>
      );
    } else {
      return (
        <Badge className="bg-red-500/10 text-red-700 border-red-200">
          <TrendingDown className="h-3 w-3 mr-1" />
          LOSS
        </Badge>
      );
    }
  };

  const getPredictionBadge = (trade: BotRealtimeTrade) => {
    if (!trade.predicted_result) {
      return (
        <Badge variant="outline" className="text-muted-foreground">
          <Minus className="h-3 w-3 mr-1" />
          No Prediction
        </Badge>
      );
    }

    // Smart detection: handle both decimal (0-1) and percentage (0-100) formats
    const confidence = trade.prediction_confidence ? 
      (trade.prediction_confidence > 1 ? 
        trade.prediction_confidence.toFixed(1) : 
        (trade.prediction_confidence * 100).toFixed(1)
      ) : '0';
    const winProb = trade.win_probability ? 
      (trade.win_probability > 1 ? 
        trade.win_probability.toFixed(1) : 
        (trade.win_probability * 100).toFixed(1)
      ) : '0';

    switch (trade.predicted_result) {
      case 'WIN':
        return (
          <div className="space-y-1">
            <Badge className="bg-blue-500/10 text-blue-700 border-blue-200">
              <Target className="h-3 w-3 mr-1" />
              WIN
            </Badge>
            <div className="text-xs text-muted-foreground">
              {winProb}% ({confidence}% conf.)
            </div>
          </div>
        );
      case 'LOSS':
        return (
          <div className="space-y-1">
            <Badge className="bg-orange-500/10 text-orange-700 border-orange-200">
              <Target className="h-3 w-3 mr-1" />
              LOSS
            </Badge>
            <div className="text-xs text-muted-foreground">
              {winProb}% ({confidence}% conf.)
            </div>
          </div>
        );
      case 'SKIP':
        return (
          <Badge className="bg-gray-500/10 text-gray-700 border-gray-200">
            <Minus className="h-3 w-3 mr-1" />
            SKIP
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="text-muted-foreground">
            <Minus className="h-3 w-3 mr-1" />
            Unknown
          </Badge>
        );
    }
  };

  const getAccuracyBadge = (trade: BotRealtimeTrade) => {
    if (!trade.predicted_result || trade.predicted_result === 'SKIP') {
      return (
        <Badge variant="outline" className="text-muted-foreground">
          <Minus className="h-3 w-3 mr-1" />
          N/A
        </Badge>
      );
    }

    const predictedWin = trade.predicted_result === 'WIN';
    const actualWin = trade.win === 1;
    const wasCorrect = (predictedWin && actualWin) || (!predictedWin && !actualWin);

    if (wasCorrect) {
      return (
        <Badge className="bg-green-500/10 text-green-700 border-green-200">
          <CheckCircle className="h-3 w-3 mr-1" />
          CORRECT
        </Badge>
      );
    } else {
      return (
        <Badge className="bg-red-500/10 text-red-700 border-red-200">
          <XCircle className="h-3 w-3 mr-1" />
          INCORRECT
        </Badge>
      );
    }
  };

  const selectedBot = linkedBots.find(bot => bot.id === currentBotId);
  const winRate = trades.length > 0 
    ? (trades.filter(t => t.win === 1).length / trades.length) * 100 
    : 0;

  const hasNextPage = (page + 1) * limit < totalTrades;
  const hasPrevPage = page > 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Real-time Trades History
            </CardTitle>
            <CardDescription>
              View recent trading signals and their results
            </CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={loadTradesHistory}
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {linkedBots.length === 0 ? (
          <div className="text-center py-8">
            <BotIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-lg font-medium mb-2">No linked bots found</p>
            <p className="text-sm text-muted-foreground">
              Link trading bots to CSV files to view their real-time trade history
            </p>
          </div>
        ) : (
          <>
            {/* Bot Selection */}
            <div className="flex items-center gap-4">
              <div className="space-y-2 flex-1">
                <label className="text-sm font-medium">Select Bot</label>
                <Select value={currentBotId} onValueChange={handleBotChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a bot..." />
                  </SelectTrigger>
                  <SelectContent>
                    {linkedBots.map((bot) => (
                      <SelectItem key={bot.id} value={bot.id}>
                        <div className="flex items-center gap-2">
                          <BotIcon className="h-4 w-4" />
                          {bot.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedBot && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold">{trades.length}</p>
                    <p className="text-xs text-muted-foreground">Recent Trades</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold">{winRate.toFixed(1)}%</p>
                    <p className="text-xs text-muted-foreground">Win Rate</p>
                  </div>
                </div>
              )}
            </div>

            {/* Trades Table */}
            {currentBotId && (
              <>
                {isLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-sm text-muted-foreground">Loading trades...</p>
                  </div>
                ) : trades.length === 0 ? (
                  <div className="text-center py-8">
                    <History className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <p className="text-lg font-medium mb-2">No trades yet</p>
                    <p className="text-sm text-muted-foreground">
                      Waiting for trading signals from {selectedBot?.name}
                    </p>
                  </div>
                ) : (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Timestamp</TableHead>
                          <TableHead>Action</TableHead>
                          <TableHead>Instrument</TableHead>
                          <TableHead>Prediction</TableHead>
                          <TableHead>Result</TableHead>
                          <TableHead>Accuracy</TableHead>
                          <TableHead>Sequence</TableHead>
                          <TableHead>Window</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Time Ago</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {trades.map((trade) => (
                          <TableRow key={trade.id}>
                            <TableCell className="font-mono text-sm">
                              {new Date(trade.timestamp).toLocaleString()}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">
                                {trade.action}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-medium">
                              {trade.instrument}
                            </TableCell>
                            <TableCell>
                              {getPredictionBadge(trade)}
                            </TableCell>
                            <TableCell>
                              {getResultBadge(trade.win)}
                            </TableCell>
                            <TableCell>
                              {getAccuracyBadge(trade)}
                            </TableCell>
                            <TableCell className="font-mono text-sm max-w-[200px]">
                              {trade.prediction_sequence ? (
                                <div className="truncate" title={trade.prediction_sequence}>
                                  {trade.prediction_sequence.length > 20 
                                    ? `${trade.prediction_sequence.substring(0, 20)}...` 
                                    : trade.prediction_sequence}
                                </div>
                              ) : (
                                <span className="text-muted-foreground">N/A</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {trade.sequence_window_used ? (
                                <Badge variant="secondary" className="text-xs">
                                  {trade.sequence_window_used}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground">N/A</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {trade.amount ? `$${trade.amount.toFixed(2)}` : '-'}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {formatDistanceToNow(new Date(trade.timestamp), { addSuffix: true })}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    {/* Pagination */}
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-muted-foreground">
                        Showing {page * limit + 1}-{Math.min((page + 1) * limit, totalTrades)} of {totalTrades} trades
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPage(page - 1)}
                          disabled={!hasPrevPage}
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPage(page + 1)}
                          disabled={!hasNextPage}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};